import { TreeNodeRouter } from './TreeNodeRouter'
import * as React from 'react'
import { INode } from 'shared/types/tree'
import { ITreeParams } from '../TreeActions'
import { Box, Button } from '@mui/material'
import { ReactNode } from 'react'
import { useNavigateTo, useParams } from 'infrastructure/routing'
// eslint-disable-next-line no-restricted-imports
import { useLocation } from 'react-router'
import { useRoutedNode, useRoutedTree, useRoutedTreeIdMap } from './hooks'
import { NodeIdMap } from '../../version_mapping/utility'

interface WrappedProps {
  tree: INode
  nodeId?: string
  reFetchTree: () => void
  idMap: NodeIdMap
}
interface WithTreeProps {
  readonly Wrapped: React.ComponentType<WrappedProps>
  readonly WithoutTree?: React.ReactNode
  readonly checkForNodeId?: boolean
}

/**
 * creates fetch parameters for getting full tree or at node in tree
 * @param info string
 * @param checkForNodeId boolean to either add/remove node id
 * @returns ITreeParams
 */
const getTreeParams = (info: string, checkForNodeId?: boolean): ITreeParams => {
  const [treeId, versionId, nodeId] = info.split(':')
  const treeParams: ITreeParams = { treeId, versionId, nodeId }

  if (!checkForNodeId || !treeParams.nodeId) {
    delete treeParams.nodeId
  }

  return treeParams
}

export function WithTree(params: WithTreeProps) {
  const { WithoutTree } = params
  const { treeVersionNodeId } = useParams()

  if (!treeVersionNodeId) {
    return <WithoutIds>{WithoutTree}</WithoutIds>
  }
  return <WithIds {...params} treeVersionNodeId={treeVersionNodeId} />
}

type WithTreeIdProps = {
  readonly Wrapped: React.ComponentType<{ treeId: string; versionId: string }>
  readonly WithoutTree?: React.ReactNode
}

export function WithTreeIds({ WithoutTree, Wrapped }: WithTreeIdProps) {
  const { treeVersionNodeId } = useParams()

  if (!treeVersionNodeId) {
    return <WithoutIds>{WithoutTree}</WithoutIds>
  }
  const treeParams = getTreeParams(treeVersionNodeId)
  return <Wrapped {...treeParams} />
}

function WithoutIds({ children }: { children: ReactNode }) {
  return (
    <Box>
      {children}
      <TreeNodeRouter />
    </Box>
  )
}

function WithIds({
  treeVersionNodeId,
  checkForNodeId,
  Wrapped,
}: {
  treeVersionNodeId: string
  checkForNodeId?: boolean
  Wrapped: React.ComponentType<WrappedProps>
}) {
  const location = useLocation()
  const treeParams = getTreeParams(treeVersionNodeId, checkForNodeId)
  const { resetTree } = useRoutedTree()
  const tree = useRoutedNode()
  const idMap = useRoutedTreeIdMap()
  const navigate = useNavigateTo()

  function clearTree() {
    const [rootPath] = location.pathname.split(`/${tree!.tree_id}`)
    navigate(rootPath)
  }

  if (!tree || !resetTree || !idMap) {
    return <Box>Loading...</Box>
  }

  return (
    <Box>
      <Box>
        <b>{tree.title}</b>
        <Button onClick={clearTree}>X</Button>
      </Box>
      <Wrapped
        tree={tree}
        nodeId={treeParams.nodeId}
        reFetchTree={resetTree}
        idMap={idMap}
      />
    </Box>
  )
}
